// Browser page translation (Chrome/Google Translate, Safari, Edge) replaces the
// text nodes React rendered with its own `<font>` elements. React still holds
// the old nodes, so its next `removeChild` or `insertBefore` on them throws
// "NotFoundError: The node to be removed is not a child of this node" (or
// Safari's "The object can not be found here.") and the whole page falls back
// to the error page (Sentry PRODUCTION-4H, -4M, -VN). React won't fix this
// itself: https://github.com/facebook/react/issues/11538.
//
// This is the well-known guard from that issue: when the node React is about
// to remove is no longer a child of the parent, skip the call, and when the
// node it inserts before is gone, append instead of throwing. That's the only case that changes behaviour, and it's
// exactly the one that used to crash; at worst a translated text stays stale
// until the next page. Components also wrap text that sits next to
// conditionally rendered elements in its own element (e.g. `Button`), which
// avoids most of these calls in the first place.

type Intercepted = "insertBefore" | "removeChild"

interface NodePrototype {
  insertBefore<T>(newNode: T, referenceNode: unknown): T
  removeChild<T>(child: T): T
}

const INSTALLED = Symbol.for("vm.translationDomGuard")

export function installTranslationDomGuard(
  prototype: NodePrototype | undefined = typeof Node === "function"
    ? (Node.prototype as unknown as NodePrototype)
    : undefined,
  onIntercept?: (method: Intercepted) => void,
): void {
  if (!prototype) return

  const marked = prototype as NodePrototype & { [INSTALLED]?: boolean }

  if (marked[INSTALLED]) return

  marked[INSTALLED] = true

  const originalRemoveChild = prototype.removeChild
  const originalInsertBefore = prototype.insertBefore

  prototype.removeChild = function <T>(this: unknown, child: T): T {
    if ((child as { parentNode?: unknown })?.parentNode !== this) {
      onIntercept?.("removeChild")

      return child
    }

    return originalRemoveChild.call(this, child) as T
  }

  prototype.insertBefore = function <T>(
    this: unknown,
    newNode: T,
    referenceNode: unknown,
  ): T {
    if (
      referenceNode &&
      (referenceNode as { parentNode?: unknown }).parentNode !== this
    ) {
      onIntercept?.("insertBefore")

      // Appended rather than dropped (as the snippet in the React issue does),
      // so new content such as an error message or the next page still shows,
      // only maybe in a different spot among the translated siblings.
      return originalInsertBefore.call(this, newNode, null) as T
    }

    return originalInsertBefore.call(this, newNode, referenceNode) as T
  }
}
